import { StrictMode } from 'react';
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary';
import { MockModeBanner } from '@/shared/components/feedback/MockModeBanner';
import { AppProviders } from './providers/AppProviders';
import { AppRouter } from './router/AppRouter';

/**
 * Application root. The outermost error boundary is the last line of defence for a failure
 * that escapes a page-level boundary, such as one thrown by a provider.
 *
 * The mock-mode banner sits above the router rather than inside the authenticated layout,
 * because the login page is exactly where mistaking fixtures for the real thing does the
 * most damage: the credentials that work here are not credentials anywhere else.
 *
 * `StrictMode` starts here rather than at the root, which is unusual enough to explain.
 * Gluestack's `StyledProvider` decides which of its instances owns the document-level colour
 * mode by writing a module-level id during render. StrictMode's deliberate double render
 * makes the discarded first pass claim that ownership, so the instance React actually keeps
 * is never the owner and stops applying the theme: `<html>` gets `gs` but never `gs-dark`,
 * and dark styles are never resolved. Wrapping our own tree instead of the whole app keeps
 * the double-render checks over every component we wrote while leaving the provider — which
 * is created inside `AppProviders` — to render once. See `UserPreferencesProvider`.
 */
export function App(): React.JSX.Element {
  return (
    <ErrorBoundary>
      <AppProviders>
        <StrictMode>
          <div className="app-root">
            <MockModeBanner />
            <div className="app-router">
              <AppRouter />
            </div>
          </div>
        </StrictMode>
      </AppProviders>
    </ErrorBoundary>
  );
}
